/**
 * One-line plain-English summaries of the two template types.
 *
 * Extracted from Templates.tsx because the deploy wizard needs exactly the same sentences: the
 * whole point of its "This deployment" card is that what it says matches what the Templates list
 * said, so an admin recognises the template they picked. A second copy would drift the moment one
 * of them learned about a new field.
 *
 * The mock calls the UX one `uxLine` (sap-mockup/components/DeployWizard.tsx:121).
 */
import type { DeploymentTemplate, IntuneDeploymentTemplate } from '../services/api'
import { plural } from './plural'

/** One line of plain English beats a row of chips — the summary has to answer "what will this
 *  actually do to my users" without opening the editor. */
export function uxSummary(t: DeploymentTemplate): string {
  const cfg = (t.config_json ?? {}) as any
  const parts: string[] = []

  // schema 2 stores the mock's shape (closeApps.mode); schema 1 stores the 013 shape
  // (interactive + welcome.*). Both are live, so both must render.
  const mode: string | undefined = cfg?.closeApps?.mode
  if (mode) {
    if (mode === 'perform') parts.push('Silent — no user interaction')
    else if (mode === 'skip') parts.push('Skips the install if apps are in use')
    else if (mode === 'autoClose') {
      parts.push('Closes apps automatically')
      parts.push(`${cfg?.closeApps?.countdownSeconds ?? 0}s countdown`)
    } else {
      // Deferral and the response timeout only mean anything in the mode that puts a dialog up
      // and lets the user postpone. Stating "3 deferrals" beside a silent install described a
      // button nobody will ever see.
      parts.push('Notifies the user')
      if (cfg?.defer?.allow) {
        const dm = cfg?.defer?.mode
        if (dm === 'indefinite') parts.push('defer indefinitely')
        else if (dm === 'deadline') parts.push(`deadline after ${plural(cfg?.defer?.deadlineDays ?? 0, 'day')}`)
        else parts.push(plural(cfg?.defer?.count ?? 0, 'deferral'))
      } else {
        parts.push('no deferral')
      }
      if (cfg?.timers?.notifyTimeoutMinutes) {
        parts.push(`${cfg.timers.notifyTimeoutMinutes} min timeout`)
      }
    }
  } else {
    parts.push(cfg?.interactive ? 'Notifies the user' : 'Silent — no user interaction')
    if (cfg?.welcome?.allowDefer) parts.push(plural(cfg?.welcome?.deferTimes ?? 0, 'deferral'))
  }

  // Always last, and always present: which language the person at the device reads the dialog in
  // is a fact about every template, and it was the one thing the summary never said.
  const lang = cfg?.identity?.languageOverride
  parts.push(lang ? `Language forced: ${lang}` : 'Auto language')
  return parts.join(' · ')
}

/**
 * The deploy wizard's one-line behaviour statement — the mock's `uxLine`
 * (sap-mockup/components/DeployWizard.tsx:74).
 *
 * Deliberately NOT `uxSummary`. The mock keeps two functions for the two jobs and so do we: the
 * Templates list answers "which template is this", where a name and a language matter; the deploy
 * card and the Overview answer "what is about to happen to the person at the keyboard", which is
 * the dialog, the postpones and the wait. Folding them into one would make one of the two screens
 * read wrong, and it is not obvious which.
 */
export function uxLine(config_json: any): string {
  const cfg = (config_json ?? {}) as any
  const parts: string[] = []

  // schema 2 stores the mock's shape (closeApps.mode); schema 1 stores the 013 shape
  // (interactive + welcome.*). Both are live, so both must render.
  const mode: string | undefined = cfg?.closeApps?.mode
  if (mode === 'perform') {
    parts.push('Silent — no dialog')
  } else if (mode === 'skip') {
    parts.push('Skips while the app is in use')
  } else if (mode === 'autoClose') {
    parts.push('Closes the app automatically')
    parts.push(`${cfg?.closeApps?.countdownSeconds ?? 0}s countdown`)
  } else if (mode) {
    parts.push('Asks the user to close it')
    parts.push(cfg?.defer?.allow ? plural(cfg?.defer?.count ?? 0, 'postpone') : 'no postpones')
    parts.push(`${cfg?.timers?.notifyTimeoutMinutes ?? 0} min wait`)
  } else if (cfg?.interactive) {
    parts.push('Asks the user to close it')
    parts.push(
      cfg?.welcome?.allowDefer ? plural(cfg?.welcome?.deferTimes ?? 0, 'postpone') : 'no postpones',
    )
  } else {
    parts.push('Silent — no dialog')
  }

  const lang = cfg?.identity?.languageOverride
  parts.push(lang ? `language ${lang}` : 'auto language')
  return parts.join(' · ')
}

/** Intent is a status, so it reads as a chip rather than as words in a sentence — the mock tones
 *  each one (its page.tsx:84-95) so a Required row and an Available row are told apart without
 *  reading. `label` is the admin-facing name, never the enum token. */
export type IntentTone = 'green' | 'blue' | 'red' | 'violet'

const INTENT_LABEL: Record<string, string> = {
  required: 'Required',
  available: 'Available',
  uninstall: 'Uninstall',
  updateOnly: 'Update only',
}
const INTENT_TONE: Record<string, IntentTone> = {
  required: 'green',
  available: 'blue',
  uninstall: 'red',
  updateOnly: 'violet',
}

const TARGET_LABEL: Record<string, string> = {
  allDevices: 'All devices',
  allUsers: 'All users',
  group: 'group',
}

/** One chip per assignment: what the intent is, and who it reaches. */
export function intuneAssignmentChips(
  t: IntuneDeploymentTemplate,
  limit = 2,
): { key: string; label: string; tone: IntentTone; more?: number }[] {
  const rows: any[] = t.config?.appAssignments ?? []
  const shown = rows.slice(0, limit).map((r, i) => ({
    key: `${r.intent}-${i}`,
    label: `${INTENT_LABEL[r.intent] ?? r.intent}: ${TARGET_LABEL[r.target] ?? r.target}`,
    tone: INTENT_TONE[r.intent] ?? ('blue' as IntentTone),
  }))
  const extra = rows.length - shown.length
  return extra > 0
    ? [...shown, { key: 'more', label: `+${extra} more`, tone: 'blue' as IntentTone, more: extra }]
    : shown
}

export function intuneSummary(
  t: IntuneDeploymentTemplate,
  opts: { omitPattern?: boolean } = {},
): string {
  const c = t.config
  const parts: string[] = []
  const rows = c?.appAssignments ?? []
  // The intents themselves are rendered as chips beside the name (intuneAssignmentChips), so the
  // sentence states the COUNT and leaves the enum tokens out of the English.
  if (rows.length) {
    parts.push(plural(rows.length, 'assignment'))
  } else {
    parts.push('no assignments')
  }
  if (c?.updateOnly?.enabled)
    parts.push(
      // Which installs it reaches is the difference between patching an estate and patching only
      // what we put there, so the summary says which — 'on' alone hides the more consequential half.
      c.updateOnly.matchUnmanaged === false
        ? 'update-only app on (Agentic Packr installs only)'
        : 'update-only app on (any install)',
    )
  if (c?.automatedUpdates?.supersede) {
    // Worth stating plainly: on a Required-only template supersedence buys rollback, not
    // updates, because Intune only auto-updates apps delivered as Available.
    const hasAvailable = rows.some((r: any) => r.intent === 'available')
    parts.push(hasAvailable ? 'auto-update on' : 'supersede (rollback only — no Available assignment)')
  }
  // The raw pattern is useful on the Templates LIST, where there is no app to resolve it
  // against. On the deploy Overview it is the F-9 complaint itself — the caller there renders the
  // resolved name instead.
  if (!opts.omitPattern && c?.naming?.appPattern) parts.push(c.naming.appPattern)
  return parts.join(' · ')
}

/**
 * The same summary, but for a raw config_json rather than a saved template — what the deploy
 * wizard shows once a rollout has ADJUSTED the settings, where there is no template row to read.
 */
export function uxSummaryFromConfig(config_json: any): string {
  return uxSummary({ config_json } as DeploymentTemplate)
}
